import { useNavigate } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Select, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { PickButton, PRIMARY_SX, SECONDARY_SX } from '../components/PickButton';
import { runnerApi, RunnerApiError } from '../lib/runnerApiClient';
import type { Me, MyApplication, MyPhotos, TransportMode } from '../lib/runnerApiClient';

/**
 * Deck §15 (`1593:2`) — "Your photo & how you arrive".
 *
 * Two spec constraints shape this screen more than anything visual:
 *   §18c the CURRENT photo stays live while a new one is in review, so the
 *        page always shows the live/submitted PAIR, never one slot that
 *        blinks between states.
 *   §18e there is no licence-plate field anywhere, deliberately. Do not add
 *        one — it is a never-render value in the same class as a UPC.
 */

const ACCENT = { green: '#2E7D32', orange: '#F67A1A', navy: '#343F5B' };
// Standalone identity-check pricing remains unconfirmed. Replacement requests
// go through Support until the approved paid verification flow is available.

const TRANSPORT_MODES: TransportMode[] = ['car'];
const modeLabel = (t: TFunction, m: TransportMode) => t(`photo.mode.${m}`);
/** Vehicle wording only makes sense for things you drive (§18e.2). */
const DRIVES: TransportMode[] = ['car', 'suv_van'];

const GOOD = ['photo.good1', 'photo.good2', 'photo.good3'];
const BAD = ['photo.bad1', 'photo.bad2', 'photo.bad3'];

type Flash = { kind: 'success' | 'error' | 'info'; msg: string } | null;

export default function PhotoAndVehicle({ me, application }: { me: Me; application: MyApplication | null }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<MyPhotos | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const [error, setError] = useState<string | null>(null);
  /** A replacement file held back until the Runner confirms the re-check fee. */
  const [confirmFile, setConfirmFile] = useState<File | null>(null);

  const load = useCallback(async (first: boolean) => {
    if (first) setLoading(true);
    try {
      setPhotos(await runnerApi.myPhotos());
      setError(null);
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('photo.loadFailed'));
    } finally {
      if (first) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load(true);
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <Spinner aria-label={t('photo.loading')} />
      </Box>
    );
  }
  if (error && !photos) return <Alert severity="error">{error}</Alert>;

  // Before the background check clears there is no profile row to hang a photo
  // on — say so plainly instead of showing a form that cannot submit.
  if (photos && !photos.ready) {
    return (
      <Box data-testid="page-photo">
        <Header />
        <Panel title={t('photo.notOpenTitle')} accent={ACCENT.navy} sx={{ maxWidth: 640 }}>
          <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>
            {t('photo.notOpenBody')}
          </Typography>
        </Panel>
      </Box>
    );
  }

  const p = photos?.profile ?? null;
  const v = photos?.vehicle ?? null;
  const vd = photos?.vehicleDetails ?? null;
  const pending = p?.status === 'pending';
  // Nothing live and nothing in review = they have never submitted one.
  const firstEver = !p?.liveUrl && !pending;

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setFlash(null);
    try {
      await fn();
      await load(false);
      setFlash({ kind: 'success', msg: ok });
    } catch (e) {
      setFlash({ kind: 'error', msg: e instanceof RunnerApiError ? e.message : t('photo.genericError') });
    } finally {
      setBusy(false);
    }
  }

  /**
   * §18c-bis: a REPLACEMENT costs the Runner a fresh ID check, so it is confirmed
   * with the amount before the upload is accepted. A first photo is covered by
   * onboarding and needs no confirmation — do not put a paywall in front of a
   * Runner who has not got a photo up yet.
   */
  function submitOrConfirm(f: File) {
    if (p?.liveUrl) {
      setConfirmFile(f);
      return;
    }
    void act(() => runnerApi.uploadPhoto('profile', f), t('photo.submitted'));
  }

  return (
    <Box data-testid="page-photo">
      <Header />
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '380px 1fr' }, gap: 2.5, alignItems: 'start' }}>
        {/* LEFT — the actual customer-facing card, not a cropped circle (§18g) */}
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Panel accent={ACCENT.green}>
            <TitleRow title={t('photo.whatCustomersSee')} pill={p?.liveUrl ? t('photo.pillLiveNow') : t('photo.pillNotLiveYet')} tone={p?.liveUrl ? 'green' : 'grey'} />
            <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mb: 2 }}>
              {t('photo.cardNote')}
            </Typography>
            <RunnerCard
              photoUrl={p?.liveUrl ?? null}
              name={me.displayName ?? (application ? `${application.firstName} ${application.lastName.charAt(0)}.` : t('photo.you'))}
              vehiclePhotoUrl={v?.status === 'live' ? v.url : null}
              vehicleLine={vehicleLine(t, vd)}
              mode={vd?.transportMode ?? 'car'}
            />
          </Panel>
          <Panel title={t('photo.whyTitle')} accent={ACCENT.navy}>
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', lineHeight: 1.6 }}>
              {t('photo.why1')}
            </Typography>
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', lineHeight: 1.6, mt: 1.5 }}>
              {es ? 'Cambiar tu foto requiere una nueva verificación de identidad. Soporte debe confirmar el costo antes de que aceptes el cambio.' : 'Changing your photo requires a fresh identity check. Support must confirm the cost before you agree to the change.'}
            </Typography>
          </Panel>
        </Box>

        {/* RIGHT — the editing surface */}
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Panel accent={ACCENT.orange}>
            <TitleRow
              title={t('photo.yourPhoto')}
              pill={pending ? t('photo.pillInReview') : p?.status === 'live' ? t('photo.pillLive') : t('photo.pillNone')}
              tone={pending ? 'amber' : p?.status === 'live' ? 'green' : 'grey'}
            />
            {p?.rejectionReason && !pending && (
              <Box sx={{ mb: 2 }}>
                {/* Shown verbatim — the reviewer wrote it for this Runner to read. */}
                <Alert severity="warning">{p.rejectionReason}</Alert>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, mb: 2.5, flexWrap: 'wrap' }}>
              {/* Don't label an empty circle "Live now" — nothing is showing to
                  anyone yet, and saying otherwise misreads as a live blank avatar. */}
              <Shot
                url={p?.liveUrl ?? null}
                label={p?.liveUrl ? t('photo.shotLiveNow') : t('photo.shotNoPhoto')}
                caption={p?.liveUrl ? t('photo.captionCustomersSee') : t('photo.captionNothing')}
              />
              {pending && <Shot url={p?.submittedUrl ?? null} label={t('photo.shotSubmitted')} caption={t('photo.captionWaiting')} ring={ACCENT.orange} />}
            </Box>
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>
              {pending
                ? t('photo.pendingNote')
                : t('photo.newNote')}
            </Typography>
            {firstEver && (
              // §18c-bis rule 3: they learn the rule at the FIRST upload, not at the
              // moment they get charged for the second one.
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" title={t('photo.firstTitle')}>
                  {es ? 'Tu primera foto forma parte del registro. Los cambios posteriores requieren una nueva verificación de identidad y confirmar su costo con soporte antes de continuar.' : 'Your first photo is part of onboarding. Later changes require a fresh identity check and confirmation of its cost with support before continuing.'}
                </Alert>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <PickButton
                label={t('photo.takePhoto')}
                capture
                primary
                disabled={busy}
                onFile={(f) => submitOrConfirm(f)}
              />
              <PickButton
                label={t('photo.chooseLibrary')}
                disabled={busy}
                onFile={(f) => submitOrConfirm(f)}
              />
              {pending && (
                <Button
                  ariaLabel={t('photo.withdraw')}
                  buttonText={t('photo.withdraw')}
                  size="small"
                  variant="text"
                  disabled={busy}
                  sx={{ color: '#4b5563', fontWeight: 700 }}
                  onClick={() => void act(() => runnerApi.withdrawPhoto('profile'), t('photo.withdrawn'))}
                />
              )}
            </Box>
          </Panel>

          <Panel title={t('photo.goodTitle')} accent={ACCENT.navy}>
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>
              {t('photo.goodIntro')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 1, md: 3 } }}>
              <Box>{GOOD.map((g) => <Rule key={g} ok text={t(g)} />)}</Box>
              <Box>{BAD.map((b) => <Rule key={b} text={t(b)} />)}</Box>
            </Box>
          </Panel>

          <VehiclePanel details={vd} vehicle={v} busy={busy} onSaved={(msg) => { setFlash({ kind: 'success', msg }); void load(false); }} onError={(msg) => setFlash({ kind: 'error', msg })} setBusy={setBusy} />
        </Box>
      </Box>

      <Dialog
        open={!!confirmFile}
        onClose={() => !busy && setConfirmFile(null)}
        ariaLabelledBy="reverify-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="reverify-title">{es ? 'Solicitar cambio de foto' : 'Request a photo change'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>
            {es ? 'Contacta a soporte para confirmar la verificación de identidad y su costo. Tu foto actual seguirá visible.' : 'Contact support to confirm identity verification and its cost. Your current photo will remain visible.'}
          </Typography>
          <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 1.5 }}>
            {t('photo.confirmBody2')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            ariaLabel={t('photo.keepCurrent')}
            buttonText={t('photo.keepCurrent')}
            variant="outlined"
            size="small"
            sx={SECONDARY_SX}
            disabled={busy}
            onClick={() => setConfirmFile(null)}
          />
          <Button
            ariaLabel={es ? 'Contactar a soporte' : 'Contact support'}
            buttonText={es ? 'Contactar a soporte' : 'Contact support'}
            variant="contained"
            size="small"
            sx={PRIMARY_SX}
            disabled={busy}
            onClick={() => {
              setConfirmFile(null);
              navigate('/help');
            }}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' } }}>
        {t('photo.title')}
      </Typography>
      <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5 }}>
        {t('photo.subtitle')}
      </Typography>
    </Box>
  );
}

function TitleRow({ title, pill, tone }: { title: string; pill: string; tone: 'green' | 'amber' | 'grey' }) {
  const c = tone === 'green'
    ? { bg: '#e7f4e8', fg: '#2E7D32' }
    : tone === 'amber'
      ? { bg: '#fdf1e3', fg: '#9a5b12' }
      : { bg: '#f1f5f9', fg: '#5f6b7a' };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937' }}>{title}</Typography>
      <Box sx={{ backgroundColor: c.bg, color: c.fg, borderRadius: 999, px: 1.25, py: 0.35, fontSize: '11px', fontWeight: 800, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
        {pill}
      </Box>
    </Box>
  );
}

/** A circular shot with its caption. Empty state is a plain disc, never a broken img. */
function Shot({ url, label, caption, ring }: { url: string | null; label: string; caption: string; ring?: string }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Box
        sx={{
          width: 96, height: 96, borderRadius: '50%', backgroundColor: '#dfe3e8',
          backgroundImage: url ? `url(${url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center',
          border: ring ? `3px solid ${ring}` : '3px solid transparent',
        }}
        role="img"
        aria-label={url ? t('photo.shotAria', { label }) : t('photo.shotAriaEmpty', { label: label.toLowerCase() })}
      />
      <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1f2937', mt: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: '12px', color: '#5f6b7a' }}>{caption}</Typography>
    </Box>
  );
}

function Rule({ ok, text }: { ok?: boolean; text: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.55 }}>
      <Box aria-hidden sx={{ color: ok ? '#2E7D32' : '#b42318', fontWeight: 800, fontSize: '13px', lineHeight: 1.4 }}>
        {ok ? '✓' : '✕'}
      </Box>
      <Typography sx={{ color: '#4b5563', fontSize: '13px', lineHeight: 1.4 }}>{text}</Typography>
    </Box>
  );
}

function vehicleLine(t: TFunction, vd: MyPhotos['vehicleDetails']): string | null {
  if (!vd) return null;
  if (!DRIVES.includes(vd.transportMode)) return modeLabel(t, vd.transportMode);
  const parts = [vd.color, vd.make, vd.model].filter(Boolean);
  return parts.length ? parts.join(' ') : modeLabel(t, vd.transportMode);
}

function RunnerCard({ photoUrl, name, vehiclePhotoUrl, vehicleLine: line, mode }: {
  photoUrl: string | null; name: string; vehiclePhotoUrl: string | null; vehicleLine: string | null; mode: TransportMode;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Box
          role="img"
          aria-label={photoUrl ? t('photo.yourPhoto') : t('photo.shotNoPhoto')}
          sx={{ width: 52, height: 52, borderRadius: '50%', flexShrink: 0, backgroundColor: '#dfe3e8', backgroundImage: photoUrl ? `url(${photoUrl})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1f2937' }}>{name}</Typography>
          {/* No rating until real deliveries exist — never invent one. */}
          <Typography sx={{ fontSize: '12px', color: '#5f6b7a' }}>{t('photo.newRunner')}</Typography>
        </Box>
      </Box>
      {line && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2 }}>
          <Box
            role="img"
            aria-label={vehiclePhotoUrl ? t('photo.yourVehicle') : t('photo.noVehiclePhoto')}
            sx={{ width: 64, height: 44, borderRadius: 1, flexShrink: 0, backgroundColor: '#dfe3e8', backgroundImage: vehiclePhotoUrl ? `url(${vehiclePhotoUrl})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {!vehiclePhotoUrl && (
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#5f6b7a', letterSpacing: '0.04em' }}>
                {modeLabel(t, mode).toUpperCase()}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>{line}</Typography>
            <Typography sx={{ fontSize: '12px', color: '#5f6b7a' }}>{t('photo.lookCurb')}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function VehiclePanel({ details, vehicle, busy, onSaved, onError, setBusy }: {
  details: MyPhotos['vehicleDetails'];
  vehicle: MyPhotos['vehicle'];
  busy: boolean;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
  setBusy: (b: boolean) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<TransportMode>(details?.transportMode ?? 'car');
  const [make, setMake] = useState(details?.make ?? '');
  const [model, setModel] = useState(details?.model ?? '');
  const [color, setColor] = useState(details?.color ?? '');
  const drives = DRIVES.includes(mode);

  const dirty =
    mode !== (details?.transportMode ?? 'car') ||
    make !== (details?.make ?? '') ||
    model !== (details?.model ?? '') ||
    color !== (details?.color ?? '');

  async function save() {
    setBusy(true);
    try {
      await runnerApi.updateVehicle({
        ...(mode !== (details?.transportMode ?? 'car') ? { transportMode: mode } : {}),
        vehicleMake: make.trim() || null,
        vehicleModel: model.trim() || null,
        vehicleColor: color.trim() || null,
      });
      onSaved(t('photo.vehicleSaved'));
    } catch (e) {
      onError(e instanceof RunnerApiError ? e.message : t('photo.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function upload(f: File) {
    setBusy(true);
    try {
      await runnerApi.uploadPhoto('vehicle', f);
      onSaved(t('photo.vehicleSubmitted'));
    } catch (e) {
      onError(e instanceof RunnerApiError ? e.message : t('photo.uploadFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel accent={ACCENT.green}>
      <TitleRow title={t('photo.arriveTitle')} pill={t('photo.pillOptional')} tone="grey" />
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>
        {t('photo.arriveIntro')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: drives ? '1fr 1fr 1fr 1fr' : '1fr' }, gap: 2 }}>
        <Box>
          <Select
            ariaLabel={t('photo.modeLabel')}
            label={t('photo.modeLabel')}
            native
            size="small"
            value={mode}
            onChange={(_e, val) => setMode(String(val) as TransportMode)}
            options={(TRANSPORT_MODES.includes(mode) ? TRANSPORT_MODES : [mode, ...TRANSPORT_MODES]).map((m) => ({ value: m, label: modeLabel(t, m) }))}
            sx={{ width: '100%' }}
          />
        </Box>
        {drives && (
          <>
            <Box><TextField label={t('photo.make')} ariaLabel={t('photo.make')} size="small" fullWidth value={make} disabled={busy} onChange={(e) => setMake(e.target.value)} placeholder="Honda" /></Box>
            <Box><TextField label={t('photo.model')} ariaLabel={t('photo.model')} size="small" fullWidth value={model} disabled={busy} onChange={(e) => setModel(e.target.value)} placeholder="CR-V" /></Box>
            <Box><TextField label={t('photo.colour')} ariaLabel={t('photo.colour')} size="small" fullWidth value={color} disabled={busy} onChange={(e) => setColor(e.target.value)} placeholder={t('photo.colourPlaceholder')} /></Box>
          </>
        )}
      </Box>

      {drives && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2.5, flexWrap: 'wrap' }}>
          <Box
            role="img"
            aria-label={vehicle?.url ? t('photo.vehiclePhotoAria') : t('photo.noVehiclePhotoYet')}
            sx={{ width: 120, height: 80, borderRadius: 1, flexShrink: 0, backgroundColor: '#dfe3e8', backgroundImage: vehicle?.url ? `url(${vehicle.url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {!vehicle?.url && (
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#5f6b7a', letterSpacing: '0.04em' }}>
                {t('photo.vehiclePhotoLabel')}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            {/* §18e.1 — the plate is a never-render field. We never store one, and
                a photo where it is readable is sent back. Say so here, plainly. */}
            <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>
              {t('photo.platesNote')}
            </Typography>
            <PickButton
              label={vehicle?.url ? t('photo.replaceVehicle') : t('photo.addVehicle')}
              disabled={busy}
              onFile={upload}
            />
            {vehicle?.status === 'pending' && (
              <Typography sx={{ color: '#9a5b12', fontSize: '12px', mt: 1 }}>{t('photo.inReview')}</Typography>
            )}
          </Box>
        </Box>
      )}

      <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 2 }}>
        {t('photo.notDriving')}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          ariaLabel={t('photo.saveArrive')}
          buttonText={t('photo.save')}
          size="small"
          variant="contained"
          disabled={busy || !dirty}
          sx={PRIMARY_SX}
          onClick={() => void save()}
        />
      </Box>
    </Panel>
  );
}
